/**
 * Shared shader library (art §3.3): hashes, value noise, fbm, Worley, 2D SDFs, line masks and hatching, colour helpers.
 * TGSL (`'use gpu'`) module-level helpers; the float ones also run on the CPU (unit tests). House rules (cheatsheet
 * §2): method vector math, `d.f32()` for integer-valued float variables, `>>>` for u32 shifts, `===`.
 */
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';

// ── hashes (integer, stable on every GPU) ────────────────────────────────────────────────────────────────────────────

/** PCG hash, u32 → u32. */
export const pcg = (v: number) => {
  'use gpu';
  const state = d.u32(v) * 747796405 + 2891336453;
  const word = ((state >>> ((state >>> 28) + 4)) ^ state) * 277803737;
  return (word >>> 22) ^ word;
};

/** Integer lattice point → [0, 1). */
export const rnd2 = (p: d.v2f) => {
  'use gpu';
  const ix = d.u32(d.i32(std.floor(p.x)) + 32768);
  const iy = d.u32(d.i32(std.floor(p.y)) + 32768);
  return d.f32(pcg(ix + pcg(iy)) & 16777215) / 16777216.0;
};

/** Integer lattice point → two values in [0, 1). */
export const rnd22 = (p: d.v2f) => {
  'use gpu';
  const ix = d.u32(d.i32(std.floor(p.x)) + 32768);
  const iy = d.u32(d.i32(std.floor(p.y)) + 32768);
  const h = pcg(ix + pcg(iy));
  const h2 = pcg(h ^ 2654435769);
  return d.vec2f(d.f32(h & 16777215) / 16777216.0, d.f32(h2 & 16777215) / 16777216.0);
};

/** Integer lattice point (3D) → [0, 1). */
export const rnd3 = (p: d.v3f) => {
  'use gpu';
  const ix = d.u32(d.i32(std.floor(p.x)) + 32768);
  const iy = d.u32(d.i32(std.floor(p.y)) + 32768);
  const iz = d.u32(d.i32(std.floor(p.z)) + 32768);
  return d.f32(pcg(ix + pcg(iy + pcg(iz))) & 16777215) / 16777216.0;
};

/** Integer lattice point (3D) → three values in [0, 1). */
export const rnd33 = (p: d.v3f) => {
  'use gpu';
  const ix = d.u32(d.i32(std.floor(p.x)) + 32768);
  const iy = d.u32(d.i32(std.floor(p.y)) + 32768);
  const iz = d.u32(d.i32(std.floor(p.z)) + 32768);
  const h = pcg(ix + pcg(iy + pcg(iz)));
  const h2 = pcg(h ^ 2654435769);
  const h3 = pcg(h2 ^ 1013904223);
  return d.vec3f(d.f32(h & 16777215) / 16777216.0, d.f32(h2 & 16777215) / 16777216.0, d.f32(h3 & 16777215) / 16777216.0);
};

// ── value noise / fbm / Worley ──────────────────────────────────────────────────────────────────────────────────────

export const vnoise = (p: d.v2f) => {
  'use gpu';
  const i = std.floor(p);
  const f = std.fract(p);
  const u = f.mul(f).mul(d.vec2f(3).sub(f.mul(2)));
  const a = rnd2(i);
  const b = rnd2(i.add(d.vec2f(1, 0)));
  const c = rnd2(i.add(d.vec2f(0, 1)));
  const e = rnd2(i.add(d.vec2f(1, 1)));
  return std.mix(std.mix(a, b, u.x), std.mix(c, e, u.x), u.y);
};

export const vnoise3 = (p: d.v3f) => {
  'use gpu';
  const i = std.floor(p);
  const f = std.fract(p);
  const u = f.mul(f).mul(d.vec3f(3).sub(f.mul(2)));
  const a0 = rnd3(i);
  const b0 = rnd3(i.add(d.vec3f(1, 0, 0)));
  const c0 = rnd3(i.add(d.vec3f(0, 1, 0)));
  const e0 = rnd3(i.add(d.vec3f(1, 1, 0)));
  const a1 = rnd3(i.add(d.vec3f(0, 0, 1)));
  const b1 = rnd3(i.add(d.vec3f(1, 0, 1)));
  const c1 = rnd3(i.add(d.vec3f(0, 1, 1)));
  const e1 = rnd3(i.add(d.vec3f(1, 1, 1)));
  const z0 = std.mix(std.mix(a0, b0, u.x), std.mix(c0, e0, u.x), u.y);
  const z1 = std.mix(std.mix(a1, b1, u.x), std.mix(c1, e1, u.x), u.y);
  return std.mix(z0, z1, u.z);
};

/** 3-octave fbm, ~0..1. */
export const fbm3o = (p: d.v2f) => {
  'use gpu';
  let s = d.f32(0);
  let amp = d.f32(0.5);
  let q = d.vec2f(p);
  for (let i = 0; i < 3; i++) {
    s += amp * vnoise(q);
    q = q.mul(2.03).add(d.vec2f(1.7, 9.2));
    amp *= 0.5;
  }
  return s / 0.875;
};

/** 2-octave fbm, ~0..1. */
export const fbm2o = (p: d.v2f) => {
  'use gpu';
  return (vnoise(p) * 0.65 + vnoise(p.mul(2.1).add(d.vec2f(17, 5))) * 0.35);
};

/** 2D Worley: x = F1, y = F2 − F1 (edge distance proxy). 3×3 cells. */
export const worley2 = (p: d.v2f) => {
  'use gpu';
  const cell = std.floor(p);
  const f = std.fract(p);
  let f1 = d.f32(8);
  let f2 = d.f32(8);
  for (let j = -1; j <= 1; j++) {
    for (let i = -1; i <= 1; i++) {
      const o = d.vec2f(d.f32(i), d.f32(j));
      const h = rnd22(cell.add(o));
      const dv = o.add(h.mul(0.8)).add(0.1).sub(f);
      const dist = std.length(dv);
      if (dist < f1) {
        f2 = f1;
        f1 = dist;
      } else if (dist < f2) {
        f2 = dist;
      }
    }
  }
  return d.vec2f(f1, f2 - f1);
};

/** 3D Worley F1 + id hash of the nearest cell (2×2×2 neighbourhood around the nearest corner). */
export const worley3 = (p: d.v3f) => {
  'use gpu';
  const base = std.floor(p.sub(0.5));
  let f1 = d.f32(8);
  let id = d.f32(0);
  for (let k = 0; k <= 1; k++) {
    for (let j = 0; j <= 1; j++) {
      for (let i = 0; i <= 1; i++) {
        const c = base.add(d.vec3f(d.f32(i), d.f32(j), d.f32(k)));
        const h = rnd33(c);
        const dist = std.length(c.add(h.mul(0.8)).add(0.1).sub(p));
        if (dist < f1) {
          f1 = dist;
          id = h.x;
        }
      }
    }
  }
  return d.vec2f(f1, id);
};

// ── 2D SDFs (negative inside) ───────────────────────────────────────────────────────────────────────────────────────

export const sdCircle = (p: d.v2f, r: number) => {
  'use gpu';
  return std.length(p) - r;
};

export const sdBox = (p: d.v2f, halfSize: d.v2f) => {
  'use gpu';
  const q = std.abs(p).sub(halfSize);
  return std.length(std.max(q, d.vec2f(0))) + std.min(std.max(q.x, q.y), 0.0);
};

/** n-pointed star with outer radius rOut and inner radius rIn (polar approximation, exact enough for AA). */
export const sdStar = (p: d.v2f, n: number, rIn: number, rOut: number) => {
  'use gpu';
  const r = std.length(p);
  const a = std.atan2(p.y, p.x);
  const seg = 6.2831853 / n;
  const k = std.abs(std.fract(a / seg + 0.5) - 0.5) * 2.0; // 0 at a tip, 1 between tips
  const edge = std.mix(rOut, rIn, std.pow(k, 0.65));
  return (r - edge) * 0.8;
};

/** Segment with tapering width (w0 at a, w1 at b). */
export const sdTaper = (p: d.v2f, a: d.v2f, b: d.v2f, w0: number, w1: number) => {
  'use gpu';
  const pa = p.sub(a);
  const ba = b.sub(a);
  const h = std.clamp(std.dot(pa, ba) / std.max(std.dot(ba, ba), 0.000001), 0.0, 1.0);
  return std.length(pa.sub(ba.mul(h))) - std.mix(w0, w1, h);
};

/** Quatrefoil: four disks of radius r at offset o around the centre. */
export const sdQuatrefoil = (p: d.v2f, o: number, r: number) => {
  'use gpu';
  const q = std.abs(p);
  const a = std.length(q.sub(d.vec2f(o, 0))) - r;
  const b = std.length(q.sub(d.vec2f(0, o))) - r;
  const c = sdBox(p, d.vec2f(o * 0.8, o * 0.8));
  return std.min(std.min(a, b), c);
};

/**
 * Pointed arch interior (art §4.10). p in px relative to the springline centre (y up), half-width w.
 * Interior = rect below the springline ∪ (disk((+k·w, 0), R) ∩ disk((−k·w, 0), R)), R = w(1 + k); k = 0 → round arch.
 */
export const sdArch = (p: d.v2f, w: number, k: number, below: number) => {
  'use gpu';
  const R = w * (1.0 + k);
  const dR = std.length(p.sub(d.vec2f(-k * w, 0))) - R;
  const dL = std.length(p.add(d.vec2f(-k * w, 0))) - R;
  const lens = std.max(dR, dL);
  // the rect reaches 1 px above the springline so the union has no zero-distance seam there
  const rect = sdBox(p.add(d.vec2f(0, below * 0.5 - 0.5)), d.vec2f(w, below * 0.5 + 0.5));
  return std.min(lens, rect);
};

/** Trefoil arch head (art §4.10): centre lobe r 0.55w at (0, 0.45w), sides r 0.45w at (±0.55w, 0.1w), over a rect. */
export const sdTrefoil = (p: d.v2f, w: number, below: number) => {
  'use gpu';
  const c = std.length(p.sub(d.vec2f(0, 0.45 * w))) - 0.55 * w;
  const q = d.vec2f(std.abs(p.x), p.y);
  const s = std.length(q.sub(d.vec2f(0.55 * w, 0.1 * w))) - 0.45 * w;
  const rect = sdBox(p.add(d.vec2f(0, below * 0.5 - 0.06 * w)), d.vec2f(w, below * 0.5 + 0.06 * w));
  return std.min(std.min(c, s), rect);
};

// ── lines and hatching ──────────────────────────────────────────────────────────────────────────────────────────────

/** 1 on the integer lines of `coord` with width `w` (in coord units, AA by `aa` coord units). */
export const lineMask = (coord: number, w: number, aa: number) => {
  'use gpu';
  const dist = std.abs(std.fract(coord + 0.5) - 0.5);
  return 1.0 - std.smoothstep(w * 0.5 - aa, w * 0.5 + aa, dist);
};

/**
 * Art §4.1-E hatching with octave blending: `pxPerUnit` = screen px per coord unit; constant on-screen density.
 */
export const hatchLines = (coord: number, pxPerUnit: number, spacingPx: number, widthPx: number) => {
  'use gpu';
  const lv = std.log2(std.max(pxPerUnit / spacingPx, 0.001));
  const f0 = std.exp2(std.floor(lv));
  const b = std.fract(lv);
  const ppu0 = pxPerUnit / f0;
  const l0 = lineMask(coord * f0, widthPx / ppu0, 0.75 / ppu0);
  const l1 = lineMask(coord * f0 * 2.0, widthPx * 2.0 / ppu0, 1.5 / ppu0);
  return std.max(l0, l1 * b);
};

// ── colour ──────────────────────────────────────────────────────────────────────────────────────────────────────────

export const luma = (c: d.v3f) => {
  'use gpu';
  return std.dot(c, d.vec3f(0.2126, 0.7152, 0.0722));
};

export const linearToSrgb3 = (c: d.v3f) => {
  'use gpu';
  const x = std.max(c, d.vec3f(0));
  const lo = x.mul(12.92);
  const hi = std.pow(x, d.vec3f(1.0 / 2.4)).mul(1.055).sub(0.055);
  return std.select(hi, lo, std.le(x, d.vec3f(0.0031308)));
};

/** Knee tonemap (art §4.12): identity below k, soft shoulder above. */
export const knee = (c: d.v3f, k: number) => {
  'use gpu';
  const x = std.max(c.sub(k), d.vec3f(0));
  const soft = x.div(x.div(1.0 - k).add(1.0)).add(k);
  return std.select(soft, c, std.le(c, d.vec3f(k)));
};

/** Three-tone Cennini mix by band weights. */
export const ramp3 = (light: d.v3f, mid: d.v3f, shadow: d.v3f, a1: number, a2: number) => {
  'use gpu';
  return std.mix(std.mix(shadow, mid, a1), light, a2);
};
